// Open symptoms manual check-in widget (OPEN_SYMPTOMS_CHECKIN_PLAN.md Task 4).
// Self-hiding when there are no open symptoms; opens OpenSymptomsCheckInModal.

import { useState } from "react";
import { BoltIcon } from "../../icons.js";
import { OpenSymptomsCheckInModal } from "../../OpenSymptomsCheckInModal.js";
import type { WidgetProps } from "../types.js";

function relativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  return `${diffWeeks}w ago`;
}

export function OpenSymptomsWidget({ patient, openSymptoms, onOpenSymptomDone }: WidgetProps) {
  const [checkInOpen, setCheckInOpen] = useState(false);

  if (openSymptoms.length === 0) return null;

  const n = openSymptoms.length;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <BoltIcon size={16} className="text-rose-500 dark:text-rose-400" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Open symptoms</h2>
        <button
          onClick={() => setCheckInOpen(true)}
          className="ml-auto rounded-full bg-rose-100 dark:bg-rose-900/40 px-3 py-1 text-xs font-medium text-rose-700 dark:text-rose-300 hover:bg-rose-200 dark:hover:bg-rose-800/40"
        >
          Check in
        </button>
      </div>
      <p className="text-sm text-rose-700 dark:text-rose-300">
        {n} symptom{n !== 1 ? "s" : ""} still being tracked
      </p>
      <ul className="mt-1 space-y-0.5">
        {openSymptoms.slice(0, 3).map((s) => {
          const displayName = s.name.charAt(0).toUpperCase() + s.name.slice(1);
          return (
            <li key={s.key} className="truncate text-xs text-gray-500 dark:text-gray-400">
              {displayName} — {s.currentSeverity}/10 — {relativeTime(s.lastUpdatedAt)}
              {s.snoozedUntil && (
                <span className="ml-1.5 rounded-full bg-gray-100 dark:bg-gray-800 px-1.5 text-[10px]">
                  snoozed
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {n > 3 && <p className="text-xs text-gray-400 dark:text-gray-500">+{n - 3} more</p>}

      {checkInOpen && (
        <OpenSymptomsCheckInModal
          patientId={patient.patientId}
          symptoms={openSymptoms}
          density={patient.proactiveSuggestions?.symptomCardDensity ?? "full"}
          onDone={onOpenSymptomDone}
          onClose={() => setCheckInOpen(false)}
        />
      )}
    </section>
  );
}
